import type {
  SmartCreationBrowserDraft,
  SmartCreationReviewEditChange,
  SmartCreationReviewWorkspace,
} from "@adpilot/shared";

interface QueuedEdit {
  changes: SmartCreationReviewEditChange[];
  mutationId: string;
  resolve(accepted: boolean): void;
}

export interface ReviewEditQueueOptions {
  getPlan(): SmartCreationBrowserDraft | null;
  canEdit(): boolean;
  project(
    plan: SmartCreationBrowserDraft,
    changes: SmartCreationReviewEditChange[],
  ): SmartCreationReviewWorkspace;
  save(
    plan: SmartCreationBrowserDraft,
    changes: SmartCreationReviewEditChange[],
    mutationId: string,
  ): Promise<SmartCreationBrowserDraft>;
  commit(plan: SmartCreationBrowserDraft): void;
  changed(workspace: SmartCreationReviewWorkspace | null, pending: boolean): void;
  /** True means a revision conflict; later commands must await an explicit reload. */
  failed(error: unknown, plan: SmartCreationBrowserDraft): Promise<boolean>;
}

/** Serial saves use acknowledged CAS versions; later clicks remain visible over older replies. */
export class ReviewEditQueue {
  private edits: QueuedEdit[] = [];
  private epoch = 0;
  private running = false;
  private idleWaiters: Array<(accepted: boolean) => void> = [];

  constructor(private readonly options: () => ReviewEditQueueOptions) {}

  isPending(): boolean {
    return this.running || this.edits.length > 0;
  }

  reset(): void {
    this.epoch += 1;
    this.edits.splice(0).forEach((edit) => {
      edit.resolve(false);
    });
    this.idleWaiters.splice(0).forEach((resolve) => {
      resolve(false);
    });
    this.running = false;
    this.options().changed(null, false);
  }

  flush(): Promise<boolean> {
    return this.isPending()
      ? new Promise((resolve) => this.idleWaiters.push(resolve))
      : Promise.resolve(true);
  }

  enqueue(changes: SmartCreationReviewEditChange[]): Promise<boolean> {
    const options = this.options();
    const plan = options.getPlan();
    if (!plan?.reviewWorkspace || !options.canEdit() || !changes.length)
      return Promise.resolve(false);
    try {
      const projection = options.project(plan, [
        ...this.edits.flatMap((edit) => edit.changes),
        ...changes,
      ]);
      const result = new Promise<boolean>((resolve) =>
        this.edits.push({ changes, resolve, mutationId: crypto.randomUUID() }),
      );
      options.changed(projection, true);
      if (!this.running) void this.run(this.epoch);
      return result;
    } catch (error) {
      void options.failed(error, plan);
      return Promise.resolve(false);
    }
  }

  private async run(epoch: number): Promise<void> {
    this.running = true;
    let allAccepted = true;
    while (this.edits.length && epoch === this.epoch) {
      const options = this.options();
      const plan = options.getPlan();
      const edit = this.edits[0]!;
      if (!plan?.reviewWorkspace || !options.canEdit()) {
        this.reset();
        return;
      }
      // Only the requested mode is optimistic; newer authoritative permission/boundary fields win.
      const changes = edit.changes.map((change) =>
        change.operation === "set_ai_action_policy" &&
        change.policy.source === "smart_creation" &&
        plan.reviewWorkspace?.aiActionPolicy?.source === "smart_creation"
          ? {
              ...change,
              policy: {
                ...plan.reviewWorkspace.aiActionPolicy,
                revision: plan.reviewWorkspace.aiActionPolicy.revision + 1,
                requestedExecutionMode: change.policy.requestedExecutionMode,
              },
            }
          : change,
      );
      try {
        const saved = await options.save(plan, changes, edit.mutationId);
        if (epoch !== this.epoch) return;
        options.commit(saved);
        this.edits.shift();
        edit.resolve(true);
      } catch (error) {
        if (epoch !== this.epoch) return;
        allAccepted = false;
        this.edits.shift();
        edit.resolve(false);
        if (await options.failed(error, plan)) {
          if (epoch !== this.epoch) return;
          this.edits.splice(0).forEach((item) => {
            item.resolve(false);
          });
        }
      }
      if (epoch !== this.epoch) return;
      const current = this.options().getPlan();
      if (this.edits.length && current?.reviewWorkspace) {
        const surviving: QueuedEdit[] = [];
        const errors: unknown[] = [];
        let projection: SmartCreationReviewWorkspace | null = null;
        for (const item of this.edits) {
          try {
            projection = this.options().project(current, [
              ...surviving.flatMap((edit) => edit.changes),
              ...item.changes,
            ]);
            surviving.push(item);
          } catch (error) {
            allAccepted = false;
            item.resolve(false);
            errors.push(error);
          }
        }
        this.edits = surviving;
        this.options().changed(projection, surviving.length > 0);
        for (const error of errors) await this.options().failed(error, current);
      }
    }
    if (epoch !== this.epoch) return;
    this.running = false;
    this.options().changed(null, false);
    this.idleWaiters.splice(0).forEach((resolve) => {
      resolve(allAccepted);
    });
  }
}
